// Client GitHub — création du repo client, secrets/variables Actions, push.
//
// Pas d'Octokit : on n'utilise que 5 endpoints REST, et le package n'avait
// jusqu'ici aucune dépendance runtime. La seule ajoutée est `libsodium-wrappers`,
// imposée par le scellage des secrets Actions (voir `sealSecret`).

import { execFileSync } from "node:child_process"
import type { Requester } from "./http"

const API = "https://api.github.com"

export interface RepoRef {
	owner: string
	repo: string
	/** Scope les secrets et variables à cet environment plutôt qu'au repo. */
	environment?: string
}

interface PublicKeyResponse {
	key_id: string
	key: string
}

/**
 * Chiffre une valeur pour un secret Actions (sealed box libsodium).
 *
 * Node n'expose pas `crypto_box_seal` : la primitive combine X25519, blake2b
 * (dérivation du nonce) et XSalsa20-Poly1305, et OpenSSL ne fournit pas ce
 * dernier. D'où la dépendance — réimplémenter du scellage crypto à la main
 * serait un mauvais échange.
 */
export async function sealSecret(
	publicKeyBase64: string,
	value: string,
): Promise<string> {
	const { default: sodium } = await import("libsodium-wrappers")
	await sodium.ready
	const sealed = sodium.crypto_box_seal(
		sodium.from_string(value),
		sodium.from_base64(publicKeyBase64, sodium.base64_variants.ORIGINAL),
	)
	return sodium.to_base64(sealed, sodium.base64_variants.ORIGINAL)
}

export class GitHubClient {
	constructor(
		private readonly token: string,
		private readonly http: Requester,
	) {}

	private call<T>(
		method: "GET" | "POST" | "PATCH" | "PUT",
		path: string,
		label: string,
		body?: unknown,
		dryRunResult?: unknown,
		allow404 = false,
	): Promise<T> {
		return this.http.request<T>({
			method,
			url: `${API}${path}`,
			label,
			body,
			dryRunResult,
			allow404,
			headers: {
				authorization: `Bearer ${this.token}`,
				accept: "application/vnd.github+json",
				"x-github-api-version": "2022-11-28",
			},
		})
	}

	/** Login du compte porteur du token — sert à détecter owner personnel vs organisation. */
	async viewerLogin(): Promise<string> {
		if (this.http.isDryRun) return "dry-run-viewer"
		const me = await this.call<{ login: string }>(
			"GET",
			"/user",
			"Identifier le compte GitHub",
		)
		return me.login
	}

	/**
	 * Portées d'un PAT classique, lues dans `x-oauth-scopes`.
	 *
	 * Rend `undefined` pour un jeton fine-grained : GitHub n'expose pas ses
	 * permissions par en-tête, elles ne sont donc pas vérifiables à l'avance.
	 */
	async tokenScopes(): Promise<string[] | undefined> {
		if (this.http.isDryRun) return undefined

		const captured: { raw?: string | null } = {}
		await this.http.request({
			method: "GET",
			url: `${API}/user`,
			label: "Vérifier les portées du jeton GitHub",
			headers: {
				authorization: `Bearer ${this.token}`,
				accept: "application/vnd.github+json",
				"x-github-api-version": "2022-11-28",
			},
			captureHeaders: (headers) => {
				captured.raw = headers.get("x-oauth-scopes")
			},
		})

		if (captured.raw == null) return undefined
		return captured.raw
			.split(",")
			.map((scope) => scope.trim())
			.filter(Boolean)
	}

	async repoExists(owner: string, repo: string): Promise<boolean> {
		if (this.http.isDryRun) return false
		const found = await this.call<{ full_name: string } | undefined>(
			"GET",
			`/repos/${owner}/${repo}`,
			`Vérifier que le repo ${owner}/${repo} n'existe pas`,
			undefined,
			undefined,
			true,
		)
		return found !== undefined
	}

	/**
	 * Crée le repo. `POST /user/repos` pour un compte personnel,
	 * `POST /orgs/{org}/repos` sinon — l'API n'a pas d'endpoint unique.
	 */
	async createRepo(input: {
		owner: string
		repo: string
		description: string
		private: boolean
		isOrg: boolean
	}): Promise<{ cloneUrl: string; defaultBranch: string }> {
		const path = input.isOrg ? `/orgs/${input.owner}/repos` : "/user/repos"
		const res = await this.call<{
			clone_url: string
			default_branch: string
		}>(
			"POST",
			path,
			`Créer le repo ${input.owner}/${input.repo}`,
			{
				name: input.repo,
				description: input.description,
				private: input.private,
				auto_init: false,
			},
			{
				clone_url: `https://github.com/${input.owner}/${input.repo}.git`,
				default_branch: "main",
			},
		)
		return { cloneUrl: res.clone_url, defaultBranch: res.default_branch }
	}

	/**
	 * Racine des secrets et variables Actions.
	 *
	 * Sans `environment`, ils vivent au niveau du repo et servent tous les jobs.
	 * Avec, ils sont scopés à cet environment et l'emportent sur ceux du repo pour
	 * les jobs qui le ciblent — c'est ce qui permet à staging d'avoir ses propres
	 * UUID Coolify sans jamais toucher à ceux de production. Noter que le segment
	 * `/actions` disparaît sur le chemin des environments : l'API ne l'utilise pas.
	 */
	private base(ref: RepoRef): string {
		return ref.environment
			? `/repos/${ref.owner}/${ref.repo}/environments/${ref.environment}`
			: `/repos/${ref.owner}/${ref.repo}/actions`
	}

	/** Crée l'environment, ou le laisse tel quel — le PUT est idempotent côté API. */
	async ensureEnvironment(ref: RepoRef, name: string): Promise<void> {
		await this.call(
			"PUT",
			`/repos/${ref.owner}/${ref.repo}/environments/${name}`,
			`Créer l'environment GitHub « ${name} »`,
			{},
			{},
		)
	}

	async setSecret(ref: RepoRef, name: string, value: string): Promise<void> {
		const key = await this.call<PublicKeyResponse>(
			"GET",
			`${this.base(ref)}/secrets/public-key`,
			"Lire la clé publique des secrets Actions",
			undefined,
			{ key_id: "dry-run-key-id", key: "" },
		)

		// En dry-run la clé est vide : on n'a rien à sceller, et on veut surtout
		// que le secret n'apparaisse jamais en clair dans le journal.
		const encrypted = this.http.isDryRun
			? "<scellé>"
			: await sealSecret(key.key, value)

		await this.call(
			"PUT",
			`${this.base(ref)}/secrets/${name}`,
			`Poser le secret Actions ${name}`,
			{ encrypted_value: encrypted, key_id: key.key_id },
			{},
		)
	}

	async setVariable(ref: RepoRef, name: string, value: string): Promise<void> {
		// L'API distingue création (POST) et mise à jour (PATCH) : sur un repo
		// neuf la variable n'existe pas, mais un re-run doit rester possible.
		// `allow404` cible l'absence sans avaler les 401/403.
		const existing = await this.call<unknown>(
			"GET",
			`${this.base(ref)}/variables/${name}`,
			`Vérifier la variable Actions ${name}`,
			undefined,
			undefined,
			true,
		)

		if (existing) {
			await this.call(
				"PATCH",
				`${this.base(ref)}/variables/${name}`,
				`Mettre à jour la variable Actions ${name}`,
				{ name, value },
				{},
			)
			return
		}

		await this.call(
			"POST",
			`${this.base(ref)}/variables`,
			`Poser la variable Actions ${name} = ${value}`,
			{ name, value },
			{},
		)
	}
}

/**
 * Premier push du repo client.
 *
 * C'est cette étape qui déclenche toute la suite : la CI build les deux images,
 * les pousse sur GHCR, puis appelle elle-même `/api/v1/deploy` (le `curl` déjà
 * présent dans `build-and-deploy-image.yml`). D'où l'ordre du provisioning —
 * les ressources Coolify et les secrets `COOLIFY_*_UUID` doivent exister AVANT
 * ce push, sinon le premier run ne déploie rien.
 *
 * Le token voyage par `http.extraHeader`, jamais dans l'URL du remote : une
 * URL `https://x-access-token:<pat>@github.com/…` finirait en clair dans
 * `.git/config`, sur disque, dans le repo qu'on vient de livrer au client.
 *
 * Et ce header est posé par l'ENVIRONNEMENT (`GIT_CONFIG_*`, git ≥ 2.31), pas
 * par `-c` : tout ce qui passe en argv est lisible dans `ps`, et surtout
 * `execFileSync` recopie la ligne de commande entière dans le `Command failed:`
 * qu'il lève — un push refusé imprimait donc le token en clair au terminal.
 */
export function pushInitialCommit(input: {
	dir: string
	cloneUrl: string
	branch: string
	token: string
	message: string
	/** Faux → le dépôt existe déjà : on ne fait que publier sa branche. */
	bootstrap: boolean
	dryRun: boolean
	log: (line: string) => void
}): void {
	const basic = Buffer.from(`x-access-token:${input.token}`).toString("base64")
	const auth = {
		GIT_CONFIG_COUNT: "1",
		GIT_CONFIG_KEY_0: "http.extraHeader",
		GIT_CONFIG_VALUE_0: `Authorization: Basic ${basic}`,
	}

	// Une greffe pousse le HEAD courant sur SA branche sans toucher au dépôt
	// local : pas de branche créée, pas de checkout déplacé. C'est ce push qui
	// déclenche la CI de l'environnement qu'on vient de provisionner.
	const steps: { args: string[]; env?: NodeJS.ProcessEnv }[] = input.bootstrap
		? [
				{ args: ["init", "-b", input.branch] },
				{ args: ["add", "-A"] },
				{
					args: [
						"-c",
						"user.name=create-wp-reactor",
						"-c",
						"user.email=noreply@wp-reactor.dev",
						"commit",
						"-m",
						input.message,
					],
				},
				{ args: ["remote", "add", "origin", input.cloneUrl] },
				{ args: ["push", "-u", "origin", input.branch], env: auth },
			]
		: [{ args: ["push", "origin", `HEAD:${input.branch}`], env: auth }]

	for (const step of steps) {
		input.log(`git ${step.args.join(" ")}`)
		if (input.dryRun) continue
		execFileSync("git", step.args, {
			cwd: input.dir,
			stdio: "inherit",
			env: step.env ? { ...process.env, ...step.env } : process.env,
		})
	}
}
